import { useState } from "react";

import { Button, Field, TextInput } from "@/components/field";
import {
	type BackfillTotals,
	useBackfill,
} from "@/features/plausibility/use-backfill";

/**
 * Rough model spend per verified clip (docs/plausibility-verification.md §9),
 * for an order-of-magnitude estimate on the preview only.
 */
const COST_PER_CLIP_USD = 0.03;

/**
 * Run plausibility verification over this organization's history: alert-level
 * measurements recorded before the verifier existed. Preview first; a run is
 * offered only for the range just previewed. Re-running is safe, since
 * anything already verified is skipped, so a preview after the queue drains
 * shows what is left.
 */
export const BackfillPanel = () => {
	const [from, setFrom] = useState("");
	const [to, setTo] = useState("");
	const [progress, setProgress] = useState<BackfillTotals | null>(null);
	const [previewedRange, setPreviewedRange] = useState<null | string>(null);
	const backfill = useBackfill(setProgress);

	const range = `${from}|${to}`;
	const totals = backfill.isPending ? progress : (backfill.data ?? progress);
	const canRun = !backfill.isPending && previewedRange === range;

	const start = (dryRun: boolean) => {
		setProgress(null);
		backfill.mutate(
			{ dryRun, ...(from && { from }), ...(to && { to }) },
			{
				onSuccess: (result) =>
					setPreviewedRange(
						result.dryRun && result.candidates > 0 ? range : null,
					),
			},
		);
	};

	const run = () => {
		const count = backfill.data?.candidates ?? 0;
		if (
			window.confirm(
				`Verify ${count} measurements? In live mode this can change severity, titles, and descriptions on past events. Customers are not notified.`,
			)
		) {
			start(false);
		}
	};

	return (
		<section className="border-secondary rounded-md border p-4">
			<h3 className="text-primary text-sm font-semibold">Backfill history</h3>
			<p className="text-secondary text-sm">
				Verify alert-level measurements that were recorded before verification
				was turned on. In live mode the results update past events the same way
				a new verification does, but never send a notification. Events whose
				severity a customer set are left alone. The worker verifies about 180
				clips an hour.
			</p>
			<div className="mt-3 flex flex-wrap items-end gap-3">
				<Field htmlFor="backfill-from" label="From (inclusive)">
					<TextInput
						id="backfill-from"
						onChange={(event) => setFrom(event.target.value)}
						type="date"
						value={from}
					/>
				</Field>
				<Field htmlFor="backfill-to" label="To (exclusive)">
					<TextInput
						id="backfill-to"
						onChange={(event) => setTo(event.target.value)}
						type="date"
						value={to}
					/>
				</Field>
				<Button
					disabled={backfill.isPending}
					onClick={() => start(true)}
					type="button"
					variant="secondary"
				>
					Preview (dry run)
				</Button>
				<Button disabled={!canRun} onClick={run} type="button">
					{backfill.isPending && totals && !totals.dryRun
						? "Queuing…"
						: "Run backfill"}
				</Button>
			</div>
			{totals && (
				<p className="text-secondary mt-2 text-sm" role="status">
					{totals.dryRun
						? `${totals.candidates} measurements to verify (about $${(
								totals.candidates * COST_PER_CLIP_USD
							).toFixed(2)})`
						: `Queued ${totals.enqueued} of ${totals.candidates} measurements${
								totals.failed > 0 ? `, ${totals.failed} failed` : ""
							}`}{" "}
					across {totals.scannedEvents} events at 5+; {totals.alreadyVerified}{" "}
					already verified
					{totals.skippedEdited > 0 &&
						`; ${totals.skippedEdited} events skipped because a customer set their severity`}
					{totals.tooRecent > 0 &&
						`; ${totals.tooRecent} recorded in the last hour left to the live verifier`}
					.{backfill.isPending && " Still counting…"}
				</p>
			)}
			{totals?.mode === "shadow" && (
				<p className="text-secondary mt-1 text-sm">
					This organization is in shadow mode: results are recorded, but
					measurements and events are left untouched.
				</p>
			)}
			{backfill.error && (
				<p className="text-error-primary mt-2 text-sm" role="alert">
					{backfill.error.message}
				</p>
			)}
		</section>
	);
};
